import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getRepository } from '../data/repository';
import type {
  Document,
  DocumentVersion,
  Profile,
  FinancialYear,
} from '../types/vault';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiX,
  FiDownload,
  FiChevronRight,
  FiZoomIn,
  FiZoomOut,
  FiRotateCw,
  FiEye,
  FiEyeOff,
  FiFileText,
  FiInfo,
  FiClock,
  FiCheck,
  FiLock,
  FiAlertCircle,
  FiRefreshCw,
  FiArchive,
} = FiIcons;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface HoverPreviewProps {
  document: Document;
  visible: boolean;
  anchorRect: DOMRect | null;
  profileName?: string;
  yearLabel?: string;
  isPinned?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onTogglePin?: () => void;
  onOpenFull?: () => void;
  onClose?: () => void;
}

export function DocumentHoverPreview({
  document,
  visible,
  anchorRect,
  profileName,
  yearLabel,
  isPinned,
  onMouseEnter,
  onMouseLeave,
  onTogglePin,
  onOpenFull,
  onClose,
}: HoverPreviewProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const blobUrlRef = useRef<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const revokeBlob = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
  }, []);

  useEffect(() => {
    if (!visible || document.sensitive) {
      revokeBlob();
      setLoading(false);
      setPreviewError(null);
      return;
    }

    const currentId = ++requestIdRef.current;
    setLoading(true);
    setPreviewError(null);

    getRepository()
      .getDocumentBlob(document.id, document.currentVersionId)
      .then(blob => {
        if (requestIdRef.current !== currentId) return;
        if (blob) {
          revokeBlob();
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        } else {
          setPreviewError('File not found.');
        }
      })
      .catch(err => {
        if (requestIdRef.current !== currentId) return;
        setPreviewError(
          err instanceof Error ? err.message : 'Failed to load preview.'
        );
      })
      .finally(() => {
        if (requestIdRef.current === currentId) setLoading(false);
      });

    return () => { requestIdRef.current++; };
  }, [visible, document.id, document.currentVersionId, document.sensitive, revokeBlob]);

  useEffect(() => { return () => revokeBlob(); }, [revokeBlob]);

  useEffect(() => {
    if (!visible) return;
    const handleDocClick = (e: MouseEvent) => {
      if (!isPinned || !previewRef.current || !anchorRect) return;
      if (previewRef.current.contains(e.target as Node)) return;
      const { clientX: x, clientY: y } = e;
      if (
        x >= anchorRect.left &&
        x <= anchorRect.right &&
        y >= anchorRect.top &&
        y <= anchorRect.bottom
      ) return;
      if (onClose) onClose();
    };
    window.document.addEventListener('mousedown', handleDocClick);
    return () => window.document.removeEventListener('mousedown', handleDocClick);
  }, [visible, isPinned, anchorRect, onClose]);

  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.document.addEventListener('keydown', handleKey);
    return () => window.document.removeEventListener('keydown', handleKey);
  }, [visible, onClose]);

  if (!visible || !anchorRect) return null;

  const isImage = document.mimeType?.startsWith('image/');
  const isPdf = document.mimeType === 'application/pdf';
  const isZip =
    document.mimeType === 'application/zip' ||
    document.mimeType === 'application/x-zip-compressed' ||
    (document.originalFilename?.toLowerCase().endsWith('.zip') ?? false);

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let pw: number;
  if (vw >= 1366) pw = 740;
  else if (vw >= 1024) pw = 600;
  else pw = Math.min(560, vw - 24);
  pw = Math.min(pw, vw - 24);

  let ph: number;
  if (vh >= 800) ph = 680;
  else ph = Math.min(600, vh - 24);
  ph = Math.min(ph, vh - 24);

  let left = anchorRect.right + 10;
  let top = anchorRect.top;
  if (left + pw > vw - 12) left = anchorRect.left - pw - 10;
  if (left < 12) left = 12;
  if (top + ph > vh - 12) top = vh - ph - 12;
  if (top < 12) top = 12;

  const content = (
    <div
      ref={previewRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onMouseEnter}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node))
          onMouseLeave?.();
      }}
      className="fixed z-40 rounded-xl shadow-2xl border overflow-hidden flex flex-col"
      role="dialog"
      aria-label={`Preview: ${document.title}`}
      tabIndex={-1}
      style={{
        left,
        top,
        width: pw,
        height: ph,
        backgroundColor: 'var(--mfv-card-surface)',
        borderColor: 'var(--mfv-border)',
        pointerEvents: 'auto',
      }}
    >
      <div
        className="px-4 py-2.5 border-b flex items-center justify-between shrink-0 bg-slate-50"
        style={{ borderColor: 'var(--mfv-border)' }}
      >
        <div className="flex items-center min-w-0 flex-1 mr-3">
          <SafeIcon
            icon={FiFileText}
            className="w-4 h-4 mr-2 shrink-0"
            style={{ color: 'var(--mfv-teal)' }}
          />
          <div className="min-w-0">
            <p
              className="text-sm font-medium truncate text-slate-900"
              title={document.title}
            >
              {document.title}
            </p>
            <p className="text-[10px] text-slate-500 truncate">
              {profileName || 'Unknown'} • {yearLabel || 'N/A'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={onOpenFull}
            className="px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--mfv-teal)]"
            style={{ color: 'var(--mfv-teal)' }}
            aria-label="Open Full Preview"
          >
            Open Full
          </button>
          <button
            onClick={onTogglePin}
            className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center focus:outline-none focus:ring-2 focus:ring-[var(--mfv-teal)] ${
              isPinned
                ? 'bg-slate-200 text-slate-800'
                : 'text-slate-600 hover:bg-slate-200'
            }`}
            aria-label={isPinned ? 'Unpin Preview' : 'Pin Preview'}
          >
            {isPinned ? 'Unpin' : 'Pin'}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-[var(--mfv-teal)]"
            aria-label="Close Preview"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 overflow-y-auto bg-slate-100/50">
        {document.sensitive ? (
          <div className="flex items-center justify-center h-full p-6">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <SafeIcon icon={FiLock} className="w-7 h-7 text-amber-600" />
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--mfv-text-main)' }}
              >
                Sensitive document
              </p>
              <p
                className="text-xs"
                style={{ color: 'var(--mfv-text-muted)' }}
              >
                Click Open Full to reveal it securely.
              </p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--mfv-teal)' }}
            />
          </div>
        ) : previewError ? (
          <div className="flex items-center justify-center h-full p-6">
            <p className="text-sm text-red-500">{previewError}</p>
          </div>
        ) : isPdf && blobUrl ? (
          <iframe
            src={blobUrl}
            className="w-full h-full border-none"
            title={document.title}
          />
        ) : isImage && blobUrl ? (
          <div className="flex items-center justify-center h-full p-3 overflow-auto">
            <img
              src={blobUrl}
              alt={document.title}
              className="max-w-full max-h-full object-contain shadow-sm border border-slate-200 bg-white"
            />
          </div>
        ) : isZip ? (
          <div className="flex items-center justify-center h-full p-6">
            <div className="text-center space-y-3">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FiArchive} className="w-7 h-7 text-slate-400" />
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--mfv-text-main)' }}
              >
                {document.originalFilename}
              </p>
              <p className="text-xs" style={{ color: 'var(--mfv-text-muted)' }}>
                {document.mimeType} • {formatFileSize(document.fileSize)}
              </p>
              <p className="text-xs" style={{ color: 'var(--mfv-text-muted)' }}>
                ZIP archive contents cannot be displayed in the preview.
                Download the ZIP file to inspect its contents.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full p-6">
            <div className="text-center space-y-3">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FiFileText} className="w-7 h-7 text-slate-400" />
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--mfv-text-main)' }}
              >
                {document.originalFilename}
              </p>
              <p className="text-xs" style={{ color: 'var(--mfv-text-muted)' }}>
                {document.mimeType} • {formatFileSize(document.fileSize)}
              </p>
              <p className="text-xs" style={{ color: 'var(--mfv-text-muted)' }}>
                This file type cannot be displayed in the quick preview.
                Open the full preview or download the file.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, window.document.body);
}

interface DocumentPreviewProps {
  documentId: string | null;
  isOpen: boolean;
  onClose: () => void;
  profiles: Profile[];
  financialYears: FinancialYear[];
}

export function DocumentPreview({
  documentId,
  isOpen,
  onClose,
  profiles,
  financialYears,
}: DocumentPreviewProps) {
  const [doc, setDoc] = useState<Document | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showMetadata, setShowMetadata] = useState(true);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const blobUrlRef = useRef<string | null>(null);

  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
  }, []);

  const loadDocument = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setPreviewError(null);
    setDownloadError(null);
    try {
      const repo = getRepository();
      const [document, versionList] = await Promise.all([
        repo.getDocument(documentId),
        repo.listDocumentVersions(documentId),
      ]);
      if (!document) {
        setPreviewError('Document not found.');
        return;
      }
      setDoc(document);
      setVersions(versionList);
      setSelectedVersionId(document.currentVersionId);
    } catch (err) {
      setPreviewError(
        err instanceof Error ? err.message : 'Failed to load document.'
      );
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (isOpen && documentId) {
      loadDocument();
      setRevealed(false);
      setZoom(1);
      setRotation(0);
    }
    if (!isOpen) {
      revokeBlobUrl();
      setDoc(null);
      setVersions([]);
      setSelectedVersionId(null);
      setPreviewError(null);
      setRevealed(false);
    }
  }, [isOpen, documentId, loadDocument, revokeBlobUrl]);

  useEffect(() => { setRevealed(false); }, [selectedVersionId]);

  useEffect(() => {
    if (!doc || !selectedVersionId) return;
    if (doc.sensitive && !revealed) {
      revokeBlobUrl();
      setPreviewError(null);
      return;
    }
    revokeBlobUrl();
    setPreviewError(null);
    let cancelled = false;

    const loadBlob = async () => {
      try {
        const repo = getRepository();
        const blob = await repo.getDocumentBlob(doc.id, selectedVersionId);
        if (cancelled) return;
        if (blob) {
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        }
      } catch (err) {
        if (!cancelled) setPreviewError('Failed to load file preview.');
      }
    };

    loadBlob();
    return () => { cancelled = true; };
  }, [doc, selectedVersionId, revealed, revokeBlobUrl]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.document.addEventListener('keydown', handleKey);
    return () => window.document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const handleDownload = async (versionId?: string) => {
    if (!doc) return;
    setDownloadError(null);
    const repo = getRepository();
    try {
      const { blob, filename } = await repo.downloadDocument(doc.id, versionId);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : 'Download failed. Please try again.'
      );
    }
  };

  const getProfileName = (id: string) =>
    profiles.find(p => p.id === id)?.displayName || 'Unknown';

  const getYearLabel = (id?: string) => {
    if (!id) return 'Not year-specific';
    const year = financialYears.find(y => y.id === id);
    return year ? `FY${year.endingYear}` : 'Unknown';
  };

  const isImage = doc?.mimeType?.startsWith('image/');
  const isPdf = doc?.mimeType === 'application/pdf';

  if (!isOpen || !documentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />

      <div
        className="relative w-full md:max-w-6xl lg:max-w-7xl md:rounded-2xl rounded-t-2xl max-h-[98vh] md:max-h-[95vh] flex flex-col shadow-2xl"
        style={{ backgroundColor: 'var(--mfv-card-surface)' }}
        role="dialog"
        aria-label={`Preview: ${doc?.title || 'Document'}`}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 md:px-6 py-3 border-b shrink-0"
          style={{
            borderColor: 'var(--mfv-border)',
            backgroundColor: 'var(--mfv-page-bg)',
          }}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={onClose}
              aria-label="Close preview"
              className="p-2 rounded-lg focus:outline-none focus:ring-2 hover:bg-slate-200"
              style={{ color: 'var(--mfv-text-muted)' }}
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h2
                className="text-base md:text-lg font-semibold truncate"
                style={{ color: 'var(--mfv-text-main)' }}
              >
                {doc?.title || 'Loading...'}
              </h2>
              <p className="text-xs truncate" style={{ color: 'var(--mfv-text-muted)' }}>
                {doc?.originalFilename} • {formatFileSize(doc?.fileSize || 0)}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleDownload(selectedVersionId || undefined)}
            disabled={!doc}
            aria-label="Download document"
            className="inline-flex items-center px-3 py-2 text-sm text-white rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50"
            style={{ backgroundColor: 'var(--mfv-primary-navy)' }}
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Download</span>
          </button>
        </div>

        {/* Download error banner */}
        {downloadError && (
          <div className="px-4 md:px-6 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <p className="text-sm text-red-700">{downloadError}</p>
            <button
              onClick={() => setDownloadError(null)}
              className="text-xs text-red-600 hover:text-red-800 font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Preview pane */}
          <div
            className="flex-1 overflow-auto relative bg-slate-100"
            ref={containerRef}
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-b-2"
                  style={{ borderColor: 'var(--mfv-teal)' }}
                />
              </div>
            ) : previewError ? (
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center space-y-4">
                  <SafeIcon
                    icon={FiAlertCircle}
                    className="w-12 h-12 mx-auto text-red-400"
                  />
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: 'var(--mfv-text-main)' }}
                  >
                    Preview Error
                  </h3>
                  <p
                    className="text-sm max-w-sm"
                    style={{ color: 'var(--mfv-text-muted)' }}
                  >
                    {previewError}
                  </p>
                  <button
                    onClick={loadDocument}
                    className="inline-flex items-center px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2"
                    style={{ backgroundColor: 'var(--mfv-primary-navy)' }}
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              </div>
            ) : doc?.sensitive && !revealed ? (
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center space-y-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                    <SafeIcon icon={FiLock} className="w-7 h-7 text-amber-600" />
                  </div>
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: 'var(--mfv-text-main)' }}
                  >
                    Sensitive Document
                  </h3>
                  <p
                    className="text-sm max-w-sm"
                    style={{ color: 'var(--mfv-text-muted)' }}
                  >
                    This document is marked as sensitive. Click below to reveal
                    the preview.
                  </p>
                  <button
                    onClick={() => setRevealed(true)}
                    className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4 mr-2" />
                    Reveal Preview
                  </button>
                </div>
              </div>
            ) : isImage && blobUrl ? (
              <div className="flex items-center justify-center h-full p-4">
                <img
                  src={blobUrl}
                  alt={doc?.title}
                  style={{
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                  className="transition-transform"
                />
              </div>
            ) : isPdf && blobUrl ? (
              <iframe
                src={blobUrl}
                className="w-full h-full"
                title={doc?.title}
              />
            ) : (
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-slate-200 rounded-2xl flex items-center justify-center mx-auto">
                    <SafeIcon
                      icon={FiFileText}
                      className="w-10 h-10 text-slate-400"
                    />
                  </div>
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: 'var(--mfv-text-main)' }}
                  >
                    Preview Not Available
                  </h3>
                  <p
                    className="text-sm max-w-sm"
                    style={{ color: 'var(--mfv-text-muted)' }}
                  >
                    This file type cannot be previewed. Download to view.
                  </p>
                  <button
                    onClick={() => handleDownload()}
                    className="inline-flex items-center px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2"
                    style={{ backgroundColor: 'var(--mfv-primary-navy)' }}
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                    Download File
                  </button>
                </div>
              </div>
            )}

            {/* Image zoom/rotate toolbar */}
            {isImage &&
              blobUrl &&
              (revealed || !doc?.sensitive) && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/90 backdrop-blur rounded-lg shadow-lg p-2">
                  <button
                    onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                    className="p-2 hover:bg-slate-100 rounded"
                    aria-label="Zoom out"
                    title="Zoom out"
                  >
                    <SafeIcon icon={FiZoomOut} className="w-4 h-4" />
                  </button>
                  <span
                    className="text-sm px-2"
                    style={{ color: 'var(--mfv-text-muted)' }}
                  >
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={() => setZoom(Math.min(3, zoom + 0.25))}
                    className="p-2 hover:bg-slate-100 rounded"
                    aria-label="Zoom in"
                    title="Zoom in"
                  >
                    <SafeIcon icon={FiZoomIn} className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-slate-300" />
                  <button
                    onClick={() => setRotation((rotation + 90) % 360)}
                    className="p-2 hover:bg-slate-100 rounded"
                    aria-label="Rotate image"
                    title="Rotate"
                  >
                    <SafeIcon icon={FiRotateCw} className="w-4 h-4" />
                  </button>
                  {doc?.sensitive && (
                    <>
                      <div className="w-px h-6 bg-slate-300" />
                      <button
                        onClick={() => setRevealed(false)}
                        className="p-2 hover:bg-slate-100 rounded"
                        aria-label="Hide sensitive content"
                        title="Hide"
                      >
                        <SafeIcon icon={FiEyeOff} className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              )}
          </div>

          {/* Details sidebar */}
          <div
            className="w-full md:w-80 lg:w-96 border-t md:border-t-0 md:border-l overflow-y-auto"
            style={{
              borderColor: 'var(--mfv-border)',
              backgroundColor: 'var(--mfv-card-surface)',
            }}
          >
            <div className="p-4 md:p-6 space-y-6">
              {/* Metadata header */}
              <div className="flex items-center justify-between">
                <h3
                  className="text-sm font-semibold"
                  style={{ color: 'var(--mfv-text-main)' }}
                >
                  Document Details
                </h3>
                <button
                  onClick={() => setShowMetadata(!showMetadata)}
                  aria-label={showMetadata ? 'Hide details' : 'Show details'}
                  className="p-1 rounded"
                  style={{ color: 'var(--mfv-text-muted)' }}
                >
                  <SafeIcon
                    icon={showMetadata ? FiChevronRight : FiInfo}
                    className="w-4 h-4"
                  />
                </button>
              </div>

              {/* Metadata fields */}
              {showMetadata && doc && (
                <div className="space-y-4">
                  <div>
                    <label
                      className="text-xs"
                      style={{ color: 'var(--mfv-text-muted)' }}
                    >
                      Profile
                    </label>
                    <p
                      className="text-sm font-medium"
                      style={{ color: 'var(--mfv-text-main)' }}
                    >
                      {getProfileName(doc.profileId)}
                    </p>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ color: 'var(--mfv-text-muted)' }}
                    >
                      Financial Year
                    </label>
                    <p
                      className="text-sm"
                      style={{ color: 'var(--mfv-text-main)' }}
                    >
                      {getYearLabel(doc.financialYearId)}
                    </p>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ color: 'var(--mfv-text-muted)' }}
                    >
                      Document Type
                    </label>
                    <p style={{ color: 'var(--mfv-text-main)' }}>
                      {doc.documentType}
                    </p>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ color: 'var(--mfv-text-muted)' }}
                    >
                      MIME Type
                    </label>
                    <p
                      className="text-sm font-mono"
                      style={{ color: 'var(--mfv-text-muted)' }}
                    >
                      {doc.mimeType}
                    </p>
                  </div>
                  {doc.tags && doc.tags.length > 0 && (
                    <div>
                      <label
                        className="text-xs"
                        style={{ color: 'var(--mfv-text-muted)' }}
                      >
                        Tags
                      </label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {doc.tags.map(tag => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {!doc.sensitive && doc.notes && (
                    <div>
                      <label
                        className="text-xs"
                        style={{ color: 'var(--mfv-text-muted)' }}
                      >
                        Notes
                      </label>
                      <p
                        className="text-sm mt-1"
                        style={{ color: 'var(--mfv-text-main)' }}
                      >
                        {doc.notes}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {doc.final && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                        <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />
                        Final
                      </span>
                    )}
                    {doc.sensitive && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        <SafeIcon icon={FiLock} className="w-3 h-3 mr-1" />
                        Sensitive
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Version history */}
              <div
                className="pt-4 border-t"
                style={{ borderColor: 'var(--mfv-border)' }}
              >
                <h3
                  className="text-sm font-semibold mb-3 flex items-center"
                  style={{ color: 'var(--mfv-text-main)' }}
                >
                  <SafeIcon
                    icon={FiClock}
                    className="w-4 h-4 mr-2"
                    style={{ color: 'var(--mfv-text-muted)' }}
                  />
                  Version History
                </h3>
                <div className="space-y-2">
                  {versions.map(version => (
                    <div
                      key={version.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedVersionId(version.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedVersionId(version.id);
                        }
                      }}
                      className={`w-full text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedVersionId === version.id
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className="text-sm font-medium"
                          style={{ color: 'var(--mfv-text-main)' }}
                        >
                          Version {version.versionNumber}
                        </span>
                        <div className="flex items-center gap-1">
                          {version.id === doc?.currentVersionId && (
                            <span
                              className="px-2 py-0.5 text-white text-xs rounded"
                              style={{ backgroundColor: 'var(--mfv-teal)' }}
                            >
                              Current
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(version.id);
                            }}
                            className="p-1 rounded hover:bg-slate-200"
                            aria-label={`Download version ${version.versionNumber}`}
                            title="Download this version"
                            style={{ color: 'var(--mfv-text-muted)' }}
                          >
                            <SafeIcon icon={FiDownload} className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <p
                        className="text-xs"
                        style={{ color: 'var(--mfv-text-muted)' }}
                      >
                        {version.originalFilename}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <span
                          className="text-xs"
                          style={{ color: 'var(--mfv-text-muted)' }}
                        >
                          {new Date(version.created).toLocaleDateString()}
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: 'var(--mfv-text-muted)' }}
                        >
                          {formatFileSize(version.fileSize)}
                        </span>
                      </div>
                      {version.reason && (
                        <p
                          className="text-xs mt-1 italic"
                          style={{ color: 'var(--mfv-text-muted)' }}
                        >
                          {version.reason}
                        </p>
                      )}
                      {version.final && (
                        <span className="inline-flex items-center mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                          <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />
                          Final
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}